// Purpose: fetch Console dashboard data from the staging API contract.
import type { BillingSummaryRow, CallRow, ConsoleNumber, RuntimeStatusRow } from './mockData';

const apiBaseUrl = import.meta.env.VITE_CONSOLE_API_BASE_URL as string | undefined;
const accountId = import.meta.env.VITE_CONSOLE_ACCOUNT_ID as string | undefined;
const subaccountId = import.meta.env.VITE_CONSOLE_SUBACCOUNT_ID as string | undefined;

export interface ConsoleTenantSelection {
  account_id: string;
  subaccount_id: string;
}

let selectedTenant: ConsoleTenantSelection | null = null;

export function setConsoleTenantSelection(selection: ConsoleTenantSelection | null): void {
  selectedTenant = selection;
}

export function getConsoleTenantSelection(): ConsoleTenantSelection | null {
  return selectedTenant;
}

function tenantHeaders(): Record<string, string> {
  const resolvedAccountId = selectedTenant?.account_id ?? accountId;
  const resolvedSubaccountId = selectedTenant?.subaccount_id ?? subaccountId;
  if (!resolvedAccountId) {
    throw new ConsoleApiError('VITE_CONSOLE_ACCOUNT_ID is not configured and no workspace is selected');
  }
  if (!resolvedSubaccountId) {
    throw new ConsoleApiError('VITE_CONSOLE_SUBACCOUNT_ID is not configured and no workspace is selected');
  }
  return {
    'X-Console-Account-Id': resolvedAccountId,
    'X-Console-Subaccount-Id': resolvedSubaccountId,
  };
}

export interface ApiNumber {
  label?: string | null;
  did: string;
  type: 'Landline' | 'Mobile';
  state: 'Live' | 'Propagating' | 'Released' | 'Suspended' | 'Unassigned';
  agent_name: string | null;
  monthly: string;
  sip_trunk_id: string | null;
  sip_trunk_name: string | null;
  trunk_assignment_status: 'pending' | 'synced' | 'error' | null;
  last_trunk_sync_error: string | null;
}

export interface ApiCall {
  id: string;
  started: string;
  caller: string;
  did: string;
  agent_name: string | null;
  status: NonNullable<CallRow['status']>;
  end_reason: CallRow['endReason'];
  duration: string;
  cost: string;
  source: CallRow['source'];
  livekit_room_name?: string;
  admission_id?: string;
  runtime_revision?: string;
}

export interface ApiRuntimeStatus {
  livekit_sip_uri: string;
  sip_dispatch: string;
  inbound_voice_proven: boolean;
  production_runtime_deployed: boolean;
}

export interface ApiRuntimeFlag {
  name: string;
  current_value: string | null;
  effective_value: string;
  default_value: string;
  allowed_values: string[];
  status: 'default' | 'overridden' | 'invalid';
  scope: string;
  category: 'rollout' | 'kill_switch' | 'performance' | 'provider_region';
  owner: string;
  description: string;
  risk: string;
  read_by: string[];
  deploy_passthrough: boolean;
  recommended_next_state: string;
}

export interface ApiRuntimeFlagsResponse {
  flags: ApiRuntimeFlag[];
  total_count: number;
  overridden_count: number;
  invalid_count: number;
}

export interface ApiCallTimelineEvent {
  title: string;
  occurred_at: string;
  detail: string;
  event_type: string;
  diagnostics: Record<string, unknown>;
}

export interface ApiCallDetail {
  call: ApiCall;
  room_name: string | null;
  answered_at: string | null;
  ended_at: string | null;
  drop_cause: string | null;
  timeline: ApiCallTimelineEvent[];
}

export interface InternalCallCostLine {
  raw_cost_id: string;
  cost_component: string;
  provider_name: string | null;
  provider_model: string | null;
  usage_unit: string;
  usage_quantity: number;
  currency: string;
  amount_micro_minor_units: number;
  amount_minor_units: number;
  pricing_status: 'priced' | 'partial' | 'unpriced';
  rate_source: string | null;
  raw_amount_minor_units: number;
}

export interface InternalCallCost {
  call_id: string;
  currency: string;
  total_amount_micro_minor_units: number;
  total_amount_minor_units: number;
  pricing_status: 'priced' | 'partial' | 'unpriced';
  unpriced_line_count: number;
  lines: InternalCallCostLine[];
  disclaimer: string;
}

export interface CallReviewData {
  transcripts: CallTranscriptSegment[];
  summary: CallSummary | null;
  recordings: CallRecording[];
  internalCost: InternalCallCost | null;
  memoryImpact: CallMemoryImpact | null;
}

export interface DashboardData {
  numbers: ConsoleNumber[];
  calls: CallRow[];
  runtimeStatus: RuntimeStatusRow;
  billingSummary: BillingSummaryRow;
}

export interface ApiAgentFolder {
  id: string;
  name: string;
  status: 'active' | 'archived';
  agent_count: number;
  created_at: string;
  updated_at: string;
}

export interface ApiAgent {
  id: string;
  name: string;
  status: 'draft' | 'active' | 'archived';
  active_version_id: string | null;
  folder_id: string | null;
  folder_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface ApiAgentVersion {
  id: string;
  agent_id: string;
  version_number: number;
  status: 'draft' | 'published' | 'archived';
  spec_json: Record<string, unknown>;
  spec_hash: string;
  published_at: string | null;
  created_at: string;
}

export interface ApiAgentWithVersions {
  agent: ApiAgent;
  versions: ApiAgentVersion[];
}

export interface ApiWebhookEndpoint {
  id: string;
  url: string;
  events: string[];
  secret_prefix: string;
  enabled: boolean;
  payload_profile?: Record<string, unknown>;
  payloadProfile?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ApiWebhookDelivery {
  id: string;
  webhook_endpoint_id: string;
  event_type: string;
  event_id: string | null;
  payload_sha256: string | null;
  status: string;
  attempts: number;
  max_attempts: number;
  next_attempt_at: string | null;
  response_status: number | null;
  delivered_at: string | null;
  dead_lettered_at: string | null;
  last_error: string | null;
  created_at: string;
}

export interface ApiDashboardInvitation {
  invitation_id: string;
  email: string;
  role: 'owner' | 'admin' | 'developer' | 'billing' | 'viewer';
  invited_by_user_id: string | null;
  accepted_by_user_id: string | null;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface ApiDashboardInvitationCreateResponse {
  invitation: ApiDashboardInvitation;
  invitation_token: string;
}

export interface ApiDashboardMember {
  membership_id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  role: 'owner' | 'admin' | 'developer' | 'billing' | 'viewer';
  created_at: string;
}

export interface ApiBillingReconciliationHistory {
  reconciliation_id: string;
  created_at: string;
  matched_call_count: number;
  mismatch_count: number;
  console_call_count: number;
  provider_usage_count: number;
  console_total_cents: number;
  provider_total_cents: number;
}

export interface ApiBillingReconciliationReport {
  reconciliation_id: string | null;
  created_at: string | null;
  matched_call_count: number;
  mismatch_count: number;
  console_call_count: number;
  provider_usage_count: number;
  console_total_cents: number;
  provider_total_cents: number;
  mismatches: Array<{ category: string; call_id: string; message: string }>;
}

export interface ApiCredential {
  id: string;
  provider: string;
  purpose: string;
  display_name: string;
  masked_secret: string | null;
  status: 'active' | 'revoked';
  created_at: string;
  updated_at: string;
  rotated_at: string | null;
  revoked_at: string | null;
}

export interface ApiTtsVoice {
  voice_id: string;
  provider: 'elevenlabs' | 'cartesia';
  provider_voice_id: string;
  display_name: string;
  voice_type: 'stock' | 'custom' | 'cloned' | 'unknown';
  supported_models: string[];
  status: 'available' | 'archived' | 'sync_error';
  metadata: Record<string, unknown>;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ApiTtsVoiceSyncResponse {
  provider: 'elevenlabs' | 'cartesia';
  synced_count: number;
  archived_count: number;
  last_synced_at: string;
  voices: ApiTtsVoice[];
}

export interface ApiMcpPermissionTool {
  name: string;
  classification: 'read_only' | 'mutating';
  allow_mutating?: boolean;
  description?: string | null;
  input_schema?: Record<string, unknown>;
}

export interface ApiMcpPermissionsUpdate {
  enabled: boolean;
  tools: ApiMcpPermissionTool[];
}

export interface ApiAgentVersionToolAttachment {
  attachment_id: string;
  agent_version_id: string;
  tool_id: string;
  tool_revision_id: string;
  display_order: number;
  attachment_config: Record<string, unknown>;
  created_at: string;
}

export interface ApiConsoleTool {
  tool_id: string;
  kind: string;
  slug: string;
  display_name: string;
  description: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface ApiSipTrunkEdge {
  edgeId: string;
  /** Only needed for edges Console manages directly during registration apply. */
  edgeProjectId: string | null;
  edgeZone: string | null;
  edgePublicIp: string;
  role: 'active' | 'backup' | 'disabled';
}

export interface ApiSipTrunk {
  sipTrunkId: string;
  name: string;
  direction: 'inbound' | 'outbound' | 'bidirectional';
  authMode: 'registration' | 'ip_allowlist';
  gatewayHost: string;
  gatewayPort: number;
  transport: 'udp' | 'tcp' | 'tls';
  carrierLabel: string | null;
  sipUsername: string | null;
  secretConfigured: boolean;
  allowedSourceRanges: string[];
  livekitProjectRef: string | null;
  livekitSipUri: string | null;
  livekitInboundTrunkId: string | null;
  livekitOutboundTrunkId: string | null;
  livekitDispatchRuleId: string | null;
  status: 'active' | 'disabled' | 'error';
  provisioningStatus: 'applying' | 'ready' | 'error';
  healthStatus: 'unknown' | 'registered' | 'unregistered' | 'failing';
  lastHealthCheckedAt: string | null;
  lastHealthError: string | null;
  activeEdge: ApiSipTrunkEdge | null;
  edges: ApiSipTrunkEdge[];
  didCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ConnectSipTrunkInput {
  name: string;
  gatewayHost: string;
  gatewayPort: number;
  transport: 'udp' | 'tcp' | 'tls';
  direction: 'inbound' | 'outbound' | 'bidirectional';
  /** Optional. Supplying both selects registration auth; omitting both means IP auth. */
  sipUsername?: string;
  sipPassword?: string;
  /** Optional display label for the carrier, e.g. "aatrox". */
  carrierLabel?: string;
  /** Optional extra source filtering. */
  allowedSourceRanges?: string[];
  /** Optional override; defaults to this environment's LiveKit SIP URI. */
  livekitSipUri?: string;
}

export interface ApiSipTrunkRegistrationPreview {
  sipTrunkId: string;
  mode: 'preview';
  targetEdgeId: string | null;
  gatewayHost: string | null;
  gatewayPort: number;
  sipUsernameMasked: string | null;
  secretConfigured: boolean;
  livekitSipUri: string | null;
  actions: string[];
  rollback: string[];
  blockers: string[];
  requiresApproval: boolean;
  planHash: string;
}

export interface ApiSipTrunkLiveKitPreview {
  sipTrunkId: string;
  mode: 'preview';
  livekitProjectRef: string | null;
  livekitSipUri: string | null;
  inboundTrunkId: string | null;
  dispatchRuleId: string | null;
  desiredNumbersSource: 'console_number_bindings';
  desiredAllowedAddresses: string[];
  changes: string[];
  unsafe: string[];
  requiresApproval: boolean;
  planHash: string;
}

export interface ApiSipTrunkApplyJob {
  maxAttempts: number;
  nextAttemptAt: string | null;
  willRetry: boolean;
  failureSummary: string | null;
  failureNextAction: string | null;
  applyJobId: string;
  sipTrunkId: string;
  target: 'registration' | 'livekit' | 'livekit_outbound';
  planHash: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  attemptCount: number;
  lastErrorCode: string | null;
  lastErrorMessage: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApiAdmissionPolicy {
  admission_paused: boolean;
  pause_reason: string | null;
  provider_spend_paused: boolean;
  provider_spend_pause_reason: string | null;
  max_active_calls: number;
  max_estimated_call_cost_cents: number;
  monthly_spend_limit_cents: number | null;
  currency: string;
  updated_at: string;
}

export interface ApiBillingSummary {
  wallet: {
    balance: string;
    available: string;
    held: string;
    currency: string;
    low_balance_threshold: string;
    updated_at: string;
  };
  is_low_balance: boolean;
  recent_ledger_entries: Array<{
    id: string;
    entry_type: string;
    amount: string;
    currency: string;
    description: string | null;
    call_id: string | null;
    created_at: string;
  }>;
  recent_call_costs: Array<{
    call_id: string;
    from_number: string | null;
    to_number: string | null;
    agent_name: string | null;
    started_at: string | null;
    duration: string;
    cost: string;
    currency: string;
    status: string;
  }>;
}

export interface ContactInteraction {
  interaction_id: string;
  contact_id: string;
  interaction_type: 'ai_call' | 'human_call' | 'manual_note' | 'external_import';
  occurred_at: string;
  direction: 'inbound' | 'outbound' | 'manual' | 'unknown';
  summary: string;
  outcome: string | null;
  next_action: string | null;
  agent_visible: boolean;
  internal_only: boolean;
  agent_id: string | null;
  call_id: string | null;
  source_system: string;
  source_record_id: string | null;
  created_at: string;
}

export interface ContactMemory {
  contact_memory_id: string;
  contact_id: string;
  revision_number: number;
  summary: string;
  agent_visible_summary: string;
  known_preferences: unknown[];
  open_items: unknown[];
  last_interaction_id: string | null;
  last_interaction_at: string | null;
  derived_at: string;
  derived_by: string;
  derivation_status: 'ready' | 'stale' | 'failed' | 'redacted';
}

export interface ContactMemoryPacket {
  contact_memory: ContactMemory | null;
  agent_contact_memory: Record<string, unknown> | null;
  recent_interactions: ContactInteraction[];
}

export interface ContactInteractionCreateInput {
  contact_id: string;
  interaction_type: 'human_call' | 'manual_note';
  occurred_at: string;
  direction: 'inbound' | 'outbound' | 'manual' | 'unknown';
  summary: string;
  outcome?: string | null;
  next_action?: string | null;
  agent_visible: boolean;
  internal_only: boolean;
  visibility_reason?: string | null;
  agent_id?: string | null;
  metadata?: Record<string, unknown>;
}

export interface ContactInteractionCreateResponse {
  interaction: ContactInteraction;
  memory: ContactMemory;
  idempotent_replay: boolean;
}

export interface ResolvedOpenItemChange {
  item: string | null;
  item_text: string | null;
  item_number: number | null;
  reason: string | null;
  opened_by_interaction_id: string | null;
}

export interface IgnoredOpenItemResolution {
  item_text: string | null;
  item_number: number | null;
  reason: string | null;
  ignored_reason: string;
}

export interface DroppedOpenItem {
  item: string | null;
  reason: string;
}

export interface OpenItemChanges {
  opened_items: string[];
  legacy_items_added: string[];
  resolved_items: ResolvedOpenItemChange[];
  ignored_resolutions: IgnoredOpenItemResolution[];
  dropped_items: DroppedOpenItem[];
}

export interface CallMemoryImpact {
  call_id: string;
  contact_id: string | null;
  agent_id: string | null;
  memory_available: boolean;
  memory_updated: boolean;
  status_reason: string;
  revision_before: number | null;
  revision_after: number | null;
  update_interaction: ContactInteraction | null;
  contact_memory: ContactMemory | null;
  open_item_changes: OpenItemChanges | null;
}

export class ConsoleApiError extends Error {
  readonly status: number | null;
  readonly path: string | null;

  constructor(message: string, options: { status?: number; path?: string } = {}) {
    super(message);
    this.name = 'ConsoleApiError';
    this.status = options.status ?? null;
    this.path = options.path ?? null;
  }
}

export interface CurrentTenantMembership {
  account_id: string;
  subaccount_id: string;
  role: 'owner' | 'admin' | 'developer' | 'billing' | 'viewer';
  account_name: string | null;
  subaccount_name: string | null;
}

export interface CurrentUser {
  user_id: string;
  email: string | null;
  authenticated: boolean;
  provider: 'google' | 'github';
  memberships: CurrentTenantMembership[];
}

export interface OAuthLoginStart {
  authorization_url: string;
  provider: 'google' | 'github';
}

export interface SubaccountProvisionResponse {
  account_id: string;
  subaccount_id: string;
  name: string;
  slug: string;
  environment: 'local' | 'staging' | 'production';
  status: 'active' | 'suspended' | 'closed';
  created_at: string;
  updated_at: string;
}

export function isUnauthorizedApiError(error: unknown): boolean {
  return error instanceof ConsoleApiError && error.status === 401;
}

async function fetchAuthJson<T>(path: string, options?: RequestInit): Promise<T> {
  if (!apiBaseUrl) {
    throw new ConsoleApiError('VITE_CONSOLE_API_BASE_URL is not configured');
  }

  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(options?.headers ?? {}),
    },
  });

  if (!response.ok) {
    const errorDetail = await response.text().catch(() => 'Unknown error');
    throw new ConsoleApiError(`Console API ${path} returned ${response.status}: ${errorDetail}`, { status: response.status, path });
  }
  return response.json() as Promise<T>;
}

async function fetchJson<T>(path: string, options?: RequestInit): Promise<T> {
  const headers = tenantHeaders();

  return fetchAuthJson<T>(path, {
    ...options,
    headers: { ...headers, ...(options?.headers ?? {}) },
  });
}

export async function fetchCurrentUser(): Promise<CurrentUser> {
  return fetchAuthJson<CurrentUser>('/auth/me');
}

export async function startOAuthLogin(provider: OAuthLoginStart['provider']): Promise<OAuthLoginStart> {
  return fetchAuthJson<OAuthLoginStart>(`/auth/login/${provider}`);
}

export async function logoutCurrentUser(): Promise<void> {
  await fetchAuthJson<{ success: boolean }>('/auth/logout', { method: 'POST' });
}

export async function provisionSubaccount(input: {
  name: string;
  slug: string;
  environment: 'local' | 'staging' | 'production';
}): Promise<SubaccountProvisionResponse> {
  return fetchJson<SubaccountProvisionResponse>('/v0/subaccounts', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Map one API number row onto the dashboard number view model. */
function toConsoleNumber(number: ApiNumber): ConsoleNumber {
  return {
    did: formatAustralianNumber(number.did),
    label: number.label ?? null,
    rawDid: number.did,
    type: number.type,
    state: number.state,
    agent: number.agent_name ?? '—',
    monthly: number.monthly,
    sipTrunkId: number.sip_trunk_id,
    sipTrunkName: number.sip_trunk_name,
    trunkAssignmentStatus: number.trunk_assignment_status,
    lastTrunkSyncError: number.last_trunk_sync_error,
    uatRole: number.did === '+61390721700' ? 'staging_test_did' : undefined,
    source: 'live_api',
  };
}

/** Load tenant numbers on their own, for screens that do not need full dashboard hydration. */
export async function fetchNumbers(): Promise<ConsoleNumber[]> {
  const apiNumbers = await fetchJson<ApiNumber[]>('/v0/numbers');
  return apiNumbers.map(toConsoleNumber);
}

/**
 * Label for the environment this dashboard is pointed at.
 *
 * Derived from the configured API base URL rather than hardcoded, because a
 * dashboard that always claims to be production makes it impossible to tell
 * which environment an operator is changing.
 */
export function consoleEnvironmentLabel(): string {
  const base = (apiBaseUrl === '/api' ? window.location.origin : apiBaseUrl ?? '').toLowerCase();
  if (base === 'https://console.callable.com.au') return 'Production';
  if (!base) return 'Unconfigured';
  if (base.includes('localhost') || base.includes('127.0.0.1')) return 'Local';
  if (base.includes('staging')) return 'Staging';
  if (base.includes('production')) return 'Production';
  return 'Unknown environment';
}

export interface ApiRecordingPolicy {
  scope: 'account' | 'subaccount' | 'agent';
  enabled: boolean;
  retentionDays: number;
  allowedArtifactTypes: string[];
  consentMode: 'disabled' | 'notice_required' | 'explicit_opt_in';
  consentNotice: string | null;
  /** True when no override exists and the fail-closed default applies. */
  inherited: boolean;
}

export async function registerNumber(input: {
  did: string;
  label?: string;
  sipTrunkId?: string;
  carrier?: string;
}): Promise<ApiNumber> {
  return fetchJson<ApiNumber>('/v0/numbers', {
    method: 'POST',
    body: JSON.stringify({
      did: input.did,
      carrier: input.carrier ?? 'aatrox',
      ...(input.label ? { label: input.label } : {}),
      ...(input.sipTrunkId ? { sipTrunkId: input.sipTrunkId } : {}),
    }),
  });
}

export async function fetchRecordingPolicy(): Promise<ApiRecordingPolicy> {
  return fetchJson<ApiRecordingPolicy>('/v0/recording-policy');
}

export async function updateRecordingPolicy(
  policy: Omit<ApiRecordingPolicy, 'scope' | 'inherited'>,
): Promise<ApiRecordingPolicy> {
  return fetchJson<ApiRecordingPolicy>('/v0/recording-policy', {
    method: 'PATCH',
    body: JSON.stringify(policy),
  });
}

export async function fetchRuntimeStatus(): Promise<ApiRuntimeStatus> {
  return fetchJson<ApiRuntimeStatus>('/v0/runtime/status');
}

export async function fetchRuntimeFlags(): Promise<ApiRuntimeFlagsResponse> {
  return fetchJson<ApiRuntimeFlagsResponse>('/v0/runtime/flags');
}

export async function fetchDashboardData(): Promise<DashboardData> {
  const [apiNumbers, apiCalls, apiRuntimeStatus, apiBillingSummary] = await Promise.all([
    fetchJson<ApiNumber[]>('/v0/numbers'),
    fetchJson<ApiCall[]>('/v0/calls'),
    fetchRuntimeStatus(),
    fetchJson<ApiBillingSummary>('/v0/billing/summary'),
  ]);

  const detailResults = await Promise.allSettled(
    apiCalls
      .filter((call) => call.source === 'real_uat')
      .map((call) => fetchJson<ApiCallDetail>(`/v0/calls/${call.id}`)),
  );
  const apiCallDetails = detailResults
    .filter((result): result is PromiseFulfilledResult<ApiCallDetail> => result.status === 'fulfilled')
    .map((result) => result.value);
  const detailsByCallId = new Map(apiCallDetails.map((detail) => [detail.call.id, detail]));

  return {
    numbers: apiNumbers.map(toConsoleNumber),
    calls: apiCalls.map((call) => {
      const detail = detailsByCallId.get(call.id);
      return {
        id: call.id,
        started: call.started,
        caller: formatAustralianNumber(call.caller),
        did: formatAustralianNumber(call.did),
        roomName: detail?.room_name ?? call.livekit_room_name,
        admissionId: admissionIdFromDetail(detail) ?? call.admission_id,
        runtimeRevision: call.runtime_revision,
        agent: call.agent_name ?? '—',
        status: call.status,
        duration: call.duration,
        endReason: call.end_reason,
        cost: call.cost,
        source: call.source,
        outcome: detail ? callOutcome(detail) : undefined,
        timeline: detail?.timeline.map((event) => ({
          label: event.title,
          at: formatTimelineTime(event.occurred_at),
          detail: event.detail,
          status: timelineStatus(event.event_type),
        })),
      };
    }),
    runtimeStatus: {
      livekitSipUri: apiRuntimeStatus.livekit_sip_uri,
      sipDispatch: apiRuntimeStatus.sip_dispatch,
      inboundVoiceProven: apiRuntimeStatus.inbound_voice_proven,
      productionRuntimeDeployed: apiRuntimeStatus.production_runtime_deployed,
    },
    billingSummary: billingSummaryFromApi(apiBillingSummary),
  };
}

function admissionIdFromDetail(detail: ApiCallDetail | undefined): string | undefined {
  if (!detail) return undefined;
  for (const event of detail.timeline) {
    const admissionId = event.diagnostics.admission_id;
    if (typeof admissionId === 'string' && admissionId.length > 0) return admissionId;
  }
  return undefined;
}

function callOutcome(detail: ApiCallDetail): string {
  if (detail.call.status === 'completed') return 'Finalized successfully in the Console call ledger.';
  if (detail.call.status === 'failed') return `Failed: ${detail.call.end_reason}`;
  if (detail.call.status === 'dropped') return `Dropped: ${detail.drop_cause ?? 'unknown'}`;
  return `Current state: ${detail.call.status}`;
}

function timelineStatus(eventType: string): 'complete' | 'warning' | 'failed' | 'pending' {
  if (eventType === 'call.failed' || eventType === 'call.dropped') return 'failed';
  if (eventType === 'call.started' || eventType === 'call.admitted') return 'pending';
  return 'complete';
}

function formatTimelineTime(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatAustralianNumber(value: string): string {
  if (!value.startsWith('+61')) {
    return value;
  }
  const local = value.replace('+61', '').replace(/\s/g, '');
  if (local.startsWith('4') && local.length === 9) {
    return `+61 ${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6)}`;
  }
  if (local.length === 9) {
    return `+61 ${local.slice(0, 1)} ${local.slice(1, 5)} ${local.slice(5)}`;
  }
  return value;
}

export async function fetchContactMemory(contactId: string, agentId?: string): Promise<ContactMemoryPacket> {
  const params = new URLSearchParams();
  if (agentId?.trim()) params.set('agent_id', agentId.trim());
  const query = params.toString();
  return fetchJson<ContactMemoryPacket>(`/v0/contacts/${encodeURIComponent(contactId)}/memory${query ? `?${query}` : ''}`);
}

export async function fetchContactInteractions(contactId: string, limit = 25): Promise<ContactInteraction[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  return fetchJson<ContactInteraction[]>(`/v0/contacts/${encodeURIComponent(contactId)}/interactions?${params.toString()}`);
}

export async function createContactInteraction(input: ContactInteractionCreateInput): Promise<ContactInteractionCreateResponse> {
  const idempotencyKey = `dashboard-contact-interaction:${input.contact_id}:${input.occurred_at}:${crypto.randomUUID()}`;
  return fetchJson<ContactInteractionCreateResponse>('/v0/contact-interactions', {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(input),
  });
}

export interface TestCallInput {
  agent_id: string;
  from_number: string;
  to_number: string;
  contact_id?: string | null;
}

export interface TestCallResponse {
  ok: boolean;
  console_call_id: string;
  livekit_room_name: string;
  sip_participant_id: string;
}

export interface ContactMemoryPreview {
  contact_id: string | null;
  resolved_from: 'contact_id' | 'phone' | 'unresolved';
  memory_available: boolean;
  revision_number: number | null;
  agent_visible_summary: string | null;
  last_interaction_at: string | null;
  recent_interaction_count: number;
  status_reason: string;
}

/** Ask whether a call to this contact or phone number would load contact memory. */
export async function fetchContactMemoryPreview(selector: { contactId?: string; phone?: string }): Promise<ContactMemoryPreview> {
  const params = new URLSearchParams();
  if (selector.contactId) params.set('contact_id', selector.contactId);
  if (selector.phone) params.set('phone', selector.phone);
  return fetchJson<ContactMemoryPreview>(`/v0/contacts/memory-preview?${params.toString()}`);
}

/** Link an E.164 phone number to a contact so future calls resolve memory automatically. */
export async function linkContactPhone(contactId: string, phone: string, options: { displayName?: string; allowRelink?: boolean } = {}): Promise<void> {
  await fetchJson<unknown>(`/v0/contacts/${encodeURIComponent(contactId)}/phone-links`, {
    method: 'POST',
    body: JSON.stringify({
      phone,
      display_name: options.displayName ?? null,
      allow_relink: options.allowRelink ?? false,
    }),
  });
}

/** Start one operator-initiated outbound test call from the dashboard. */
export async function createTestCall(input: TestCallInput): Promise<TestCallResponse> {
  return fetchJson<TestCallResponse>('/v0/test-calls', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// Agent management API functions

export async function fetchWebhookEndpoints(): Promise<ApiWebhookEndpoint[]> {
  return fetchJson<ApiWebhookEndpoint[]>('/v0/webhooks');
}

export async function createWebhookEndpoint(input: { url: string; events: string[]; signingSecret: string; enabled: boolean }): Promise<ApiWebhookEndpoint> {
  return fetchJson<ApiWebhookEndpoint>('/v0/webhooks', { method: 'POST', body: JSON.stringify(input) });
}

export async function fetchWebhookDeliveries(): Promise<ApiWebhookDelivery[]> {
  return fetchJson<ApiWebhookDelivery[]>('/v0/webhook-deliveries');
}

export async function fetchDashboardMembers(): Promise<ApiDashboardMember[]> {
  return fetchJson<ApiDashboardMember[]>('/v0/members');
}

export async function fetchDashboardInvitations(): Promise<ApiDashboardInvitation[]> {
  return fetchJson<ApiDashboardInvitation[]>('/v0/invitations');
}

export async function createDashboardInvitation(input: {
  email: string;
  role: ApiDashboardInvitation['role'];
  expires_in_hours: number;
}): Promise<ApiDashboardInvitationCreateResponse> {
  return fetchJson<ApiDashboardInvitationCreateResponse>('/v0/invitations', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function acceptDashboardInvitation(invitationToken: string): Promise<{ success: boolean }> {
  return fetchAuthJson<{ success: boolean }>('/auth/invitations/accept', {
    method: 'POST',
    body: JSON.stringify({ invitation_token: invitationToken }),
  });
}

export async function updateDashboardMemberRole(
  membershipId: string,
  role: ApiDashboardMember['role'],
): Promise<ApiDashboardMember> {
  return fetchJson<ApiDashboardMember>(`/v0/members/${membershipId}`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });
}

export async function revokeDashboardMember(membershipId: string): Promise<ApiDashboardMember> {
  return fetchJson<ApiDashboardMember>(`/v0/members/${membershipId}`, { method: 'DELETE' });
}

export async function fetchBillingReconciliations(): Promise<ApiBillingReconciliationHistory[]> {
  return fetchJson<ApiBillingReconciliationHistory[]>('/v0/billing/reconciliations');
}

export async function fetchInternalCallCost(callId: string): Promise<InternalCallCost | null> {
  try {
    return await fetchJson<InternalCallCost>(`/v0/calls/${callId}/internal-costs`);
  } catch (error) {
    if (error instanceof ConsoleApiError && error.status === 404) return null;
    throw error;
  }
}

export async function createBillingReconciliation(): Promise<ApiBillingReconciliationReport> {
  return fetchJson<ApiBillingReconciliationReport>('/v0/billing/reconciliation', {
    method: 'POST',
    body: JSON.stringify({ console_usage: [], provider_usage: [] }),
  });
}

export async function enqueueLowBalanceEvent(): Promise<{ queued_delivery_count: number }> {
  return fetchJson<{ queued_delivery_count: number }>('/v0/billing/low-balance-events', {
    method: 'POST',
  });
}

export async function fetchAgentFolders(): Promise<ApiAgentFolder[]> {
  return fetchJson<ApiAgentFolder[]>('/v0/agent-folders');
}

export async function createAgentFolder(name: string): Promise<ApiAgentFolder> {
  return fetchJson<ApiAgentFolder>('/v0/agent-folders', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function updateAgentFolder(folderId: string, update: { name?: string; status?: ApiAgentFolder['status'] }): Promise<ApiAgentFolder> {
  return fetchJson<ApiAgentFolder>(`/v0/agent-folders/${folderId}`, {
    method: 'PATCH',
    body: JSON.stringify(update),
  });
}

export async function deleteAgentFolder(folderId: string): Promise<ApiAgentFolder> {
  return fetchJson<ApiAgentFolder>(`/v0/agent-folders/${folderId}`, { method: 'DELETE' });
}

export async function assignAgentFolder(agentId: string, folderId: string | null): Promise<ApiAgent> {
  return fetchJson<ApiAgent>(`/v0/agents/${agentId}/folder`, {
    method: 'PATCH',
    body: JSON.stringify({ folder_id: folderId }),
  });
}

export async function fetchAgents(): Promise<ApiAgent[]> {
  return fetchJson<ApiAgent[]>('/v0/agents');
}

export async function createAgent(name: string): Promise<ApiAgent> {
  return fetchJson<ApiAgent>('/v0/agents', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function fetchAgentWithVersions(agentId: string): Promise<ApiAgentWithVersions> {
  return fetchJson<ApiAgentWithVersions>(`/v0/agents/${agentId}`);
}

export async function createAgentVersion(
  agentId: string,
  specJson: Record<string, unknown>
): Promise<ApiAgentVersion> {
  return fetchJson<ApiAgentVersion>(`/v0/agents/${agentId}/versions`, {
    method: 'POST',
    body: JSON.stringify({ spec_json: specJson }),
  });
}

export async function publishAgentVersion(
  agentId: string,
  versionId: string
): Promise<ApiAgentVersion> {
  return fetchJson<ApiAgentVersion>(`/v0/agents/${agentId}/versions/${versionId}/publish`, {
    method: 'POST',
  });
}

export async function fetchAgentVersionToolAttachments(versionId: string): Promise<ApiAgentVersionToolAttachment[]> {
  return fetchJson<ApiAgentVersionToolAttachment[]>(`/v0/agents/versions/${versionId}/tools`);
}

export async function fetchTools(): Promise<ApiConsoleTool[]> {
  return fetchJson<ApiConsoleTool[]>('/v0/tools');
}

export async function createTool(input: { kind: ApiConsoleTool['kind']; slug: string; display_name: string; description?: string }): Promise<ApiConsoleTool> {
  return fetchJson<ApiConsoleTool>('/v0/tools', { method: 'POST', body: JSON.stringify(input) });
}

export async function createToolRevision(toolId: string, spec: Record<string, unknown>): Promise<unknown> {
  return fetchJson<unknown>(`/v0/tools/${toolId}/revisions`, { method: 'POST', body: JSON.stringify({ spec }) });
}

export async function archiveAgent(agentId: string): Promise<ApiAgent> {
  return fetchJson<ApiAgent>(`/v0/agents/${agentId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status: 'archived' }),
  });
}

export async function archiveAgentVersion(agentId: string, versionId: string): Promise<ApiAgentVersion> {
  return fetchJson<ApiAgentVersion>(`/v0/agents/${agentId}/versions/${versionId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status: 'archived' }),
  });
}

export async function updateAgentVersionMcpPermissions(
  versionId: string,
  attachmentId: string,
  permissions: ApiMcpPermissionsUpdate,
): Promise<ApiAgentVersionToolAttachment> {
  return fetchJson<ApiAgentVersionToolAttachment>(
    `/v0/agents/versions/${versionId}/tools/${attachmentId}/mcp-permissions`,
    {
      method: 'PUT',
      body: JSON.stringify(permissions),
    },
  );
}

export async function fetchAdmissionPolicy(): Promise<ApiAdmissionPolicy> {
  return fetchJson<ApiAdmissionPolicy>('/v0/runtime/admission-policy');
}

export async function updateAdmissionPolicy(
  update: {
    admission_paused?: boolean;
    pause_reason?: string | null;
    provider_spend_paused?: boolean;
    provider_spend_pause_reason?: string | null;
  },
): Promise<ApiAdmissionPolicy> {
  return fetchJson<ApiAdmissionPolicy>('/v0/runtime/admission-policy', {
    method: 'PATCH',
    body: JSON.stringify(update),
  });
}

export async function fetchSipTrunks(): Promise<ApiSipTrunk[]> {
  return fetchJson<ApiSipTrunk[]>('/v0/sip-trunks');
}

export async function connectSipTrunk(input: ConnectSipTrunkInput): Promise<ApiSipTrunk> {
  return fetchJson<ApiSipTrunk>('/v0/sip-trunks', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function previewSipTrunkRegistration(
  sipTrunkId: string,
): Promise<ApiSipTrunkRegistrationPreview> {
  return fetchJson<ApiSipTrunkRegistrationPreview>(
    `/v0/sip-trunks/${sipTrunkId}/registration-preview`,
    { method: 'POST' },
  );
}

export async function previewSipTrunkLiveKit(
  sipTrunkId: string,
): Promise<ApiSipTrunkLiveKitPreview> {
  return fetchJson<ApiSipTrunkLiveKitPreview>(
    `/v0/sip-trunks/${sipTrunkId}/livekit-preview`,
    { method: 'POST' },
  );
}

export async function setSipTrunkEdges(
  sipTrunkId: string,
  edges: ApiSipTrunkEdge[],
): Promise<ApiSipTrunk> {
  return fetchJson<ApiSipTrunk>(`/v0/sip-trunks/${sipTrunkId}/edges`, {
    method: 'POST',
    body: JSON.stringify({ edges }),
  });
}

export async function applySipTrunkRegistration(
  sipTrunkId: string,
  previewPlanHash: string,
): Promise<ApiSipTrunkApplyJob> {
  return fetchJson<ApiSipTrunkApplyJob>(`/v0/sip-trunks/${sipTrunkId}/registration-apply`, {
    method: 'POST',
    body: JSON.stringify({ previewPlanHash }),
  });
}

export async function applySipTrunkLiveKit(
  sipTrunkId: string,
  previewPlanHash: string,
): Promise<ApiSipTrunkApplyJob> {
  return fetchJson<ApiSipTrunkApplyJob>(`/v0/sip-trunks/${sipTrunkId}/livekit-apply`, {
    method: 'POST',
    body: JSON.stringify({ previewPlanHash }),
  });
}

export async function assignNumberToAgent(did: string, agentId: string | null): Promise<ApiNumber> {
  return fetchJson<ApiNumber>(`/v0/numbers/${encodeURIComponent(did)}/assignment`, {
    method: 'PATCH',
    body: JSON.stringify({ agent_id: agentId }),
  });
}

export async function assignNumberToTrunk(
  did: string,
  sipTrunkId: string | null,
  agentId: string | null,
): Promise<ApiNumber> {
  return fetchJson<ApiNumber>(`/v0/numbers/${encodeURIComponent(did)}/trunk-assignment`, {
    method: 'PATCH',
    body: JSON.stringify({ sipTrunkId, agentId }),
  });
}

function billingSummaryFromApi(summary: ApiBillingSummary): BillingSummaryRow {
  return {
    balance: summary.wallet.balance,
    available: summary.wallet.available,
    held: summary.wallet.held,
    lowBalanceThreshold: summary.wallet.low_balance_threshold,
    currency: summary.wallet.currency,
    isLowBalance: summary.is_low_balance,
    updatedAt: formatDateTime(summary.wallet.updated_at),
    recentLedgerEntries: summary.recent_ledger_entries.map((entry) => ({
      id: entry.id,
      entryType: entry.entry_type,
      amount: entry.amount,
      description: entry.description,
      callId: entry.call_id,
      createdAt: formatDateTime(entry.created_at),
    })),
    recentCallCosts: summary.recent_call_costs.map((cost) => ({
      callId: cost.call_id,
      fromNumber: cost.from_number ? formatAustralianNumber(cost.from_number) : null,
      toNumber: cost.to_number ? formatAustralianNumber(cost.to_number) : null,
      agentName: cost.agent_name,
      startedAt: cost.started_at ? formatDateTime(cost.started_at) : null,
      duration: cost.duration,
      cost: cost.cost,
      status: cost.status,
    })),
  };
}

function formatDateTime(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export interface CallTranscriptSegment {
  transcript_id: string;
  speaker_role: 'user' | 'agent' | 'system';
  speaker_id: string | null;
  utterance_text: string;
  start_time_ms: number;
  end_time_ms: number | null;
  confidence: number | null;
  language_code: string | null;
  sequence: number;
}

export interface CallSummary {
  summary_id: string;
  summary_text: string;
  outcome: Record<string, unknown>;
  action_items: unknown[];
  extracted_fields: Record<string, unknown>;
  model_provider: string | null;
  model_name: string | null;
  generated_at: string;
}

export interface CallRecording {
  recording_id: string;
  storage_provider: string;
  storage_reference: string | null;
  recording_type: string;
  duration_ms: number | null;
  format: string | null;
  storage_size_bytes: number | null;
  encrypted: boolean;
  available_until: string | null;
}

export interface CallRecordingAccessUrl {
  recording_id: string;
  url: string;
  expires_at: string;
}

export async function fetchCallTranscripts(callId: string): Promise<CallTranscriptSegment[]> {
  return fetchJson<CallTranscriptSegment[]>(`/v0/calls/${callId}/transcripts`);
}

export async function fetchCallSummary(callId: string): Promise<CallSummary | null> {
  try {
    return await fetchJson<CallSummary>(`/v0/calls/${callId}/summary`);
  } catch (error) {
    if (error instanceof ConsoleApiError && error.message.includes('404')) {
      return null;
    }
    throw error;
  }
}

export async function fetchCallRecordings(callId: string): Promise<CallRecording[]> {
  return fetchJson<CallRecording[]>(`/v0/calls/${callId}/recordings`);
}

export async function fetchCallMemoryImpact(callId: string): Promise<CallMemoryImpact> {
  return fetchJson<CallMemoryImpact>(`/v0/calls/${callId}/memory-impact`);
}

export async function fetchCallRecordingPlaybackUrl(callId: string, recordingId: string): Promise<CallRecordingAccessUrl> {
  return fetchJson<CallRecordingAccessUrl>(`/v0/calls/${callId}/recordings/${recordingId}/playback-url`, {
    method: 'POST',
  });
}

export async function fetchCredentials(): Promise<ApiCredential[]> {
  return fetchJson<ApiCredential[]>('/v0/credentials');
}

export async function fetchTtsVoices(provider?: ApiTtsVoice['provider']): Promise<ApiTtsVoice[]> {
  const query = provider ? `?provider=${encodeURIComponent(provider)}` : '';
  return fetchJson<ApiTtsVoice[]>(`/v0/voices${query}`);
}

export async function syncTtsVoices(provider: ApiTtsVoice['provider']): Promise<ApiTtsVoiceSyncResponse> {
  return fetchJson<ApiTtsVoiceSyncResponse>('/v0/voices/sync', {
    method: 'POST',
    body: JSON.stringify({ provider }),
  });
}

export async function revokeCredential(credentialId: string): Promise<ApiCredential> {
  return fetchJson<ApiCredential>(`/v0/credentials/${credentialId}`, { method: 'DELETE' });
}
